import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { IChipsV2Props } from './Chips.v2.types';

export enum CHIP_THEME {
	LIGHT = 'light',
	DARK = 'dark',
}

export enum COLOR {
	GREY = 'grey',
	RED = 'red',
	ORANGE = 'orange',
	PEACH = 'peach',
	YELLOW = 'yellow',
	NEON = 'neon',
	GREEN = 'green',
	AQUA = 'aqua',
	TEAL = 'teal',
	BLUE = 'blue',
	VIOLET = 'violet',
	PURPLE = 'purple',
	LILAC = 'lilac',
}

const outlineConfigMap = {
	[`${CHIP_THEME.LIGHT}-${COLOR.GREY}`]: `${defaultTheme.v2.color.neutral[900]}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.RED}`]: `${defaultTheme.v2.color.alternateMid.red}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.ORANGE}`]: `${defaultTheme.v2.color.alternateMid.orange}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.PEACH}`]: `${defaultTheme.v2.color.alternateMid.peach}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.YELLOW}`]: `${defaultTheme.v2.color.alternateMid.yellow}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.NEON}`]: `${defaultTheme.v2.color.alternateMid.neon}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.GREEN}`]: `${defaultTheme.v2.color.alternateMid.green}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.AQUA}`]: `${defaultTheme.v2.color.alternateMid.aqua}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.TEAL}`]: `${defaultTheme.v2.color.alternateMid.teal}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.BLUE}`]: `${defaultTheme.v2.color.alternateMid.blue}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.VIOLET}`]: `${defaultTheme.v2.color.alternateMid.violet}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.PURPLE}`]: `${defaultTheme.v2.color.alternateMid.purple}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.LILAC}`]: `${defaultTheme.v2.color.alternateMid.lilac}`,
	[`${CHIP_THEME.DARK}-${COLOR.GREY}`]: `${defaultTheme.v2.color.neutral[300]}`,
	[`${CHIP_THEME.DARK}-${COLOR.RED}`]: `${defaultTheme.v2.color.altLow.red}`,
	[`${CHIP_THEME.DARK}-${COLOR.ORANGE}`]: `${defaultTheme.v2.color.altLow.orange}`,
	[`${CHIP_THEME.DARK}-${COLOR.PEACH}`]: `${defaultTheme.v2.color.altLow.peach}`,
	[`${CHIP_THEME.DARK}-${COLOR.YELLOW}`]: `${defaultTheme.v2.color.altLow.yellow}`,
	[`${CHIP_THEME.DARK}-${COLOR.NEON}`]: `${defaultTheme.v2.color.altLow.neon}`,
	[`${CHIP_THEME.DARK}-${COLOR.GREEN}`]: `${defaultTheme.v2.color.altLow.green}`,
	[`${CHIP_THEME.DARK}-${COLOR.AQUA}`]: `${defaultTheme.v2.color.altLow.aqua}`,
	[`${CHIP_THEME.DARK}-${COLOR.TEAL}`]: `${defaultTheme.v2.color.altLow.teal}`,
	[`${CHIP_THEME.DARK}-${COLOR.BLUE}`]: `${defaultTheme.v2.color.altLow.blue}`,
	[`${CHIP_THEME.DARK}-${COLOR.VIOLET}`]: `${defaultTheme.v2.color.altLow.violet}`,
	[`${CHIP_THEME.DARK}-${COLOR.PURPLE}`]: `${defaultTheme.v2.color.altLow.purple}`,
	[`${CHIP_THEME.DARK}-${COLOR.LILAC}`]: `${defaultTheme.v2.color.altLow.lilac}`,
};

const backgroundConfigMap = {
	[`${CHIP_THEME.LIGHT}-${COLOR.GREY}`]: `${defaultTheme.v2.color.neutral[300]}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.RED}`]: `${defaultTheme.v2.color.altLow.red}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.ORANGE}`]: `${defaultTheme.v2.color.altLow.orange}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.PEACH}`]: `${defaultTheme.v2.color.altLow.peach}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.YELLOW}`]: `${defaultTheme.v2.color.altLow.yellow}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.NEON}`]: `${defaultTheme.v2.color.altLow.neon}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.GREEN}`]: `${defaultTheme.v2.color.altLow.green}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.AQUA}`]: `${defaultTheme.v2.color.altLow.aqua}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.TEAL}`]: `${defaultTheme.v2.color.altLow.teal}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.BLUE}`]: `${defaultTheme.v2.color.altLow.blue}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.VIOLET}`]: `${defaultTheme.v2.color.altLow.violet}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.PURPLE}`]: `${defaultTheme.v2.color.altLow.purple}`,
	[`${CHIP_THEME.LIGHT}-${COLOR.LILAC}`]: `${defaultTheme.v2.color.altLow.lilac}`,
	[`${CHIP_THEME.DARK}-${COLOR.GREY}`]: `${defaultTheme.v2.color.neutral[900]}`,
	[`${CHIP_THEME.DARK}-${COLOR.RED}`]: `${defaultTheme.v2.color.altHigh.red}`,
	[`${CHIP_THEME.DARK}-${COLOR.ORANGE}`]: `${defaultTheme.v2.color.altHigh.orange}`,
	[`${CHIP_THEME.DARK}-${COLOR.PEACH}`]: `${defaultTheme.v2.color.altHigh.peach}`,
	[`${CHIP_THEME.DARK}-${COLOR.YELLOW}`]: `${defaultTheme.v2.color.altHigh.yellow}`,
	[`${CHIP_THEME.DARK}-${COLOR.NEON}`]: `${defaultTheme.v2.color.altHigh.neon}`,
	[`${CHIP_THEME.DARK}-${COLOR.GREEN}`]: `${defaultTheme.v2.color.altHigh.green}`,
	[`${CHIP_THEME.DARK}-${COLOR.AQUA}`]: `${defaultTheme.v2.color.altHigh.aqua}`,
	[`${CHIP_THEME.DARK}-${COLOR.TEAL}`]: `${defaultTheme.v2.color.altHigh.teal}`,
	[`${CHIP_THEME.DARK}-${COLOR.BLUE}`]: `${defaultTheme.v2.color.altHigh.blue}`,
	[`${CHIP_THEME.DARK}-${COLOR.VIOLET}`]: `${defaultTheme.v2.color.altHigh.violet}`,
	[`${CHIP_THEME.DARK}-${COLOR.PURPLE}`]: `${defaultTheme.v2.color.altHigh.purple}`,
	[`${CHIP_THEME.DARK}-${COLOR.LILAC}`]: `${defaultTheme.v2.color.altHigh.lilac}`,
};

const ChipsV2 = ({
	className,
	children,
	iconSize,
	prefixIcon,
	suffixIcon,
	size,
	iconPlacement,
	bold,
	onClick,
	'data-testid': dataTestId,
}: IChipsV2Props) => {
	const isPrefixPredefinedIcon = typeof prefixIcon === 'string';
	const isSuffixPredefinedIcon = typeof suffixIcon === 'string';

	const shouldDisplayLeftIcon = iconPlacement
		? iconPlacement === 'left' || iconPlacement === 'both'
		: true;
	const shouldDisplayRightIcon = iconPlacement
		? iconPlacement === 'right' || iconPlacement === 'both'
		: true;

	const renderIcon = (icon: PredefinedIcons | React.ReactNode, isPredefined: boolean) => {
		if (!icon) {
			return null;
		}

		if (isPredefined) {
			return <Icon size={iconSize} variant={icon as PredefinedIcons} />;
		}
		return icon;
	};

	return (
		<div className={className} onClick={onClick}>
			<PrefixWrapper>
				{shouldDisplayLeftIcon && renderIcon(prefixIcon, isPrefixPredefinedIcon)}
			</PrefixWrapper>
			<Text
				disableVariantMapping
				variant={size === 's' ? 'sans/11/400' : 'sans/14/400'}
				bold={bold}
				data-testid={dataTestId}
			>
				{children}
			</Text>
			<SuffixWrapper>
				{shouldDisplayRightIcon && renderIcon(suffixIcon, isSuffixPredefinedIcon)}
			</SuffixWrapper>
		</div>
	);
};

const PrefixWrapper = styled.div``;
const SuffixWrapper = styled.div``;

const Memoized = memo(ChipsV2);
const Styled = styled(Memoized)`
	display: flex;
	justify-content: center;
	align-items: center;
	width: fit-content;
	//MIP-947 disable outline
	background-color: ${({ chipTheme, color }) => {
		// if (outline) {
		//     return `backgroundColor: transparent`;
		// }
		return backgroundConfigMap[`${chipTheme}-${color}`];
	}};
	padding: 0.25rem 0.5rem;
	border-radius: 1rem;
	//MIP-947 disable outline
	/* ${({ chipTheme, color, outline }) => {
		if (outline) {
			return css`
				outline: 1px solid ${outlineConfigMap[`${chipTheme}-${color}`]};
			`;
		}
	}}; */
	--icon-margin: ${({ children }) => (children ? '0.25rem' : '')};
	${PrefixWrapper} {
		display: contents;
		& > * {
			margin-right: var(--icon-margin);
		}
	}
	${SuffixWrapper} {
		display: contents;
		& > * {
			margin-left: var(--icon-margin);
		}
	}

	${({ size }) => {
		switch (size) {
			case 's': {
				return `
                 ${Icon}.svg {
                    width: 0.75rem;
                    height: 0.75rem;
    }`;
			}
			case 'm': {
				return `
                 ${Icon}.svg {
                    width: 1rem;
                    height: 1rem;
    }`;
			}
		}
	}}
	--color:    ${({ chipTheme, color }) => {
		return chipTheme === CHIP_THEME.DARK
			? backgroundConfigMap[`${CHIP_THEME.LIGHT}-${color}`]
			: backgroundConfigMap[`${CHIP_THEME.DARK}-${color}`];
	}};
	${Icon}.svg {
		${({ isFilled = true }) =>
			isFilled &&
			`
            fill: var(--color);
    `}
		color: var(--color);
	}

	& > ${Text} {
		white-space: nowrap;
		color: var(--color);
	}

	${({ onClick }) => !!onClick && 'cursor: pointer;'};
`;

export { Styled as Chips };
